import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import {
  ApiKeyScope,
  ScopeDescription,
} from '../../../models/api-key/dto/api-key.dto';
import { CreateApiKeyResponseDto } from '../../../models/api-key/dto/create-api-key.dto';
import { copy } from '../copy';
import { useCreateApiKey } from '../hooks/useCreateApiKey';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  scopes: ScopeDescription[];
  onSuccess: (result: CreateApiKeyResponseDto) => void;
};

type ExpiryPreset = 'never' | '30' | '60' | '90';

export function CreateApiKeyModal({
  isOpen,
  onClose,
  organizationId,
  scopes,
  onSuccess,
}: Props) {
  const toast = useToast();
  const [name, setName] = useState('');
  // Default: all available scopes checked. Matches the legacy key's behavior
  // (grants everything) — operator can untick to narrow.
  const [selectedScopes, setSelectedScopes] = useState<Set<ApiKeyScope>>(
    () => new Set(scopes.map((s) => s.scope)),
  );
  const [expiry, setExpiry] = useState<ExpiryPreset>('never');
  const [nameError, setNameError] = useState<string | null>(null);
  const [scopesError, setScopesError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useCreateApiKey(organizationId);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setSelectedScopes(new Set(scopes.map((s) => s.scope)));
      setExpiry('never');
      setNameError(null);
      setScopesError(null);
      setServerError(null);
    }
  }, [isOpen, scopes]);

  const toggleScope = (scope: ApiKeyScope) => {
    const next = new Set(selectedScopes);
    if (next.has(scope)) next.delete(scope);
    else next.add(scope);
    setSelectedScopes(next);
    if (next.size > 0) setScopesError(null);
  };

  const handleSubmit = useCallback(async () => {
    setNameError(null);
    setScopesError(null);
    setServerError(null);

    let hasError = false;
    if (!name.trim()) {
      setNameError(copy.create.nameRequired);
      hasError = true;
    }
    if (selectedScopes.size === 0) {
      setScopesError(copy.create.scopesRequired);
      hasError = true;
    }
    if (hasError) return;

    let expiresAt: string | undefined;
    if (expiry !== 'never') {
      const days = parseInt(expiry, 10);
      expiresAt = new Date(Date.now() + days * 86_400_000).toISOString();
    }

    try {
      const result = await createMutation.mutateAsync({
        name: name.trim(),
        scopes: Array.from(selectedScopes),
        expiresAt,
      });
      onSuccess(result);
    } catch (error) {
      const message = (error as Error)?.message ?? copy.create.serverError;
      if (message.toLowerCase().includes('limit')) {
        toast({
          title: message,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        onClose();
        return;
      }
      setServerError(message);
    }
  }, [
    name,
    selectedScopes,
    expiry,
    createMutation,
    onSuccess,
    toast,
    onClose,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader>{copy.create.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={5}>
            <FormControl isInvalid={!!nameError} isRequired>
              <FormLabel>{copy.create.nameLabel}</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={copy.create.namePlaceholder}
                maxLength={64}
                autoFocus
                required={false}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl isInvalid={!!scopesError} isRequired>
              <FormLabel>{copy.create.scopesLabel}</FormLabel>
              <Stack spacing={2}>
                {scopes.map((s) => {
                  const local = copy.scopes[s.scope];
                  return (
                    <Checkbox
                      key={s.scope}
                      isChecked={selectedScopes.has(s.scope)}
                      onChange={() => toggleScope(s.scope)}
                    >
                      <Stack spacing={0}>
                        <Text fontWeight={600}>{local?.label ?? s.label}</Text>
                        <Text fontSize="12px" color="gray.600">
                          {local?.description ?? s.description}
                        </Text>
                      </Stack>
                    </Checkbox>
                  );
                })}
              </Stack>
              {scopesError && (
                <FormErrorMessage>{scopesError}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>{copy.create.expiryLabel}</FormLabel>
              <Select
                value={expiry}
                onChange={(e) => setExpiry(e.target.value as ExpiryPreset)}
              >
                <option value="never">{copy.create.expiryNever}</option>
                <option value="30">{copy.create.expiry30}</option>
                <option value="60">{copy.create.expiry60}</option>
                <option value="90">{copy.create.expiry90}</option>
              </Select>
            </FormControl>

            {serverError && (
              <Alert status="error" borderRadius="8px">
                <AlertIcon />
                <AlertDescription fontSize="13px">
                  {serverError}
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            onClick={onClose}
            isDisabled={createMutation.isLoading}
          >
            {copy.create.cancel}
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSubmit}
            isLoading={createMutation.isLoading}
          >
            {copy.create.submit}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
